import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

import Content from '../../components/Content';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import Table from '../../components/Table';
import PcapModal from '../../components/PcapModal';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';

import { getPcapList, downloadPcap, startPcap, stopPcap } from '../../redux/actions/pcapActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import classes from './PcapViewer.module.css';

const FRONTEND_PCAP_VIEWER_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	LIST_FETCH_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	REFRESH_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	DOWNLOAD_FAILED: ErrorCodes.API_INVALID_PARAMETER,
};

const buildPcapViewerSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const getErrorMessage = (source, fallbackMessage) => {
	if (typeof source === 'string' && source.trim()) {
		return source.trim();
	}

	if (source && typeof source === 'object') {
		if (typeof source.msg === 'string' && source.msg.trim()) {
			return source.msg.trim();
		}
		if (typeof source.message === 'string' && source.message.trim()) {
			return source.message.trim();
		}
	}

	return fallbackMessage;
};

const PcapViewer = () => {
	const dispatch = useDispatch();

	// Loading and error states
	const [initialLoading, setInitialLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	// Data states
	const [pcapList, setPcapList] = useState([]);
	const pcapListRef = useRef([]);

	// Capture-control states
	const [watchers, setWatchers] = useState([]);
	const [selectedWatcher, setSelectedWatcher] = useState('');
	const [captureFilter, setCaptureFilter] = useState('');
	const [maxMinutes, setMaxMinutes] = useState(30);
	const [busyAction, setBusyAction] = useState('');

	// Modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedPcapFilename, setSelectedPcapFilename] = useState(null);
	const [supportErrorMessage, setSupportErrorMessage] = useState('');
	const [copiedSupportError, setCopiedSupportError] = useState(false);

	const setPcapViewerSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildPcapViewerSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearPcapViewerSupportError = React.useCallback((force = false) => {
		if (!force) {
			return;
		}
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	const notifyPcapViewerError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		const formattedError = buildPcapViewerSupportError(errorCode, pageInfo, sourceMessage);
		setSupportErrorMessage(formattedError);
		setCopiedSupportError(false);
		toast.error(formattedError);
	}, []);

	// Fetch pcap list
	const fetchPcapList = React.useCallback(async (options = {}) => {
		const {
			showToast = false,
			failureCode = FRONTEND_PCAP_VIEWER_ERROR_CODES.LIST_FETCH_FAILED,
			failurePageInfo = 'PCAP Viewer Page / PCAP List Load Failed',
			requestErrorPageInfo = 'PCAP Viewer Page / PCAP List Request Error',
		} = options;

		try {
			const data = await dispatch(getPcapList());
			if (data && data.status) {
				const nextPcapList = data.pcaps || [];
				pcapListRef.current = nextPcapList;
				setPcapList(nextPcapList);
				if (Array.isArray(data.watchers)) {
					setWatchers(data.watchers);
					// No auto-selection of the first watcher: the dropdown is populated
					// but starts on the "Select a Watcher" placeholder. Preserve any
					// existing selection (e.g. after a refresh) without defaulting.
				}
				if (data.maxMinutes) setMaxMinutes(data.maxMinutes);
				setHasError(false);
				if (showToast) {
					toast.success('PCAP list refreshed');
				}
			} else {
				const hasExistingData = Array.isArray(pcapListRef.current) && pcapListRef.current.length > 0;
				setHasError(!hasExistingData);
				if (showToast) {
					notifyPcapViewerError(
						failureCode,
						failurePageInfo,
						getErrorMessage(data, 'Failed to fetch PCAP list')
					);
				} else {
					setPcapViewerSupportError(
						failureCode,
						failurePageInfo,
						getErrorMessage(data, 'Failed to fetch PCAP list')
					);
				}
			}
		} catch (error) {
			const hasExistingData = Array.isArray(pcapListRef.current) && pcapListRef.current.length > 0;
			setHasError(!hasExistingData);
			if (showToast) {
				notifyPcapViewerError(
					failureCode,
					requestErrorPageInfo,
					getErrorMessage(error, 'Error fetching PCAP list')
				);
			} else {
				setPcapViewerSupportError(
					failureCode,
					requestErrorPageInfo,
					getErrorMessage(error, 'Error fetching PCAP list')
				);
			}
		}
	}, [dispatch, notifyPcapViewerError, setPcapViewerSupportError]);

	// Initial data fetch
	useEffect(() => {
		const fetchInitialData = async () => {
			setInitialLoading(true);
			setHasError(false);
			await fetchPcapList({
				showToast: false,
				failureCode: FRONTEND_PCAP_VIEWER_ERROR_CODES.INITIAL_LOAD_FAILED,
				failurePageInfo: 'PCAP Viewer Page / Initial PCAP List Load Failed',
				requestErrorPageInfo: 'PCAP Viewer Page / Initial PCAP List Request Error',
			});
			setInitialLoading(false);
		};

		fetchInitialData();
	}, [fetchPcapList]);

	// Handle refresh
	const handleRefresh = async () => {
		setIsRefreshing(true);
		await fetchPcapList({
			showToast: true,
			failureCode: FRONTEND_PCAP_VIEWER_ERROR_CODES.REFRESH_FAILED,
			failurePageInfo: 'PCAP Viewer Page / Refresh PCAP List Failed',
			requestErrorPageInfo: 'PCAP Viewer Page / Refresh PCAP List Request Error',
		});
		setIsRefreshing(false);
	};

	const handleStartCapture = async () => {
		if (!selectedWatcher) {
			toast.error('Select a watcher first');
			return;
		}
		setBusyAction('start');
		const data = await dispatch(startPcap(selectedWatcher, captureFilter.trim()));
		setBusyAction('');
		if (data && data.status) {
			toast.success(data.msg || 'Capture started');
			await fetchPcapList({});
		} else {
			toast.error((data && data.msg) || 'Could not start capture');
		}
	};

	const handleStopCapture = async () => {
		if (!selectedWatcher) return;
		setBusyAction('stop');
		const data = await dispatch(stopPcap(selectedWatcher));
		setBusyAction('');
		if (data && data.status) {
			toast.success(data.msg || 'Stop requested');
			await fetchPcapList({});
		} else {
			toast.error((data && data.msg) || 'Could not stop capture');
		}
	};

	// Is a capture currently running on the selected watcher?
	const activeOnSelected = pcapList.some(
		(p) => (p.status === 'capturing' || p.status === 'requested')
	);

	// Client-side pagination for the PCAP files table
	const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(pcapList);

	// Handle download
	const handleDownload = (filename) => {
		if (!filename) {
			notifyPcapViewerError(
				FRONTEND_PCAP_VIEWER_ERROR_CODES.DOWNLOAD_FAILED,
				'PCAP Viewer Page / Download Request Missing Filename',
				'PCAP filename is required before download can start'
			);
			return;
		}
		downloadPcap(filename);
		toast.success(`Downloading ${filename}...`);
	};

	// Handle view
	const handleView = (filename) => {
		setSelectedPcapFilename(filename);
		setModalOpen(true);
	};

	// Handle modal close
	const handleModalClose = () => {
		setModalOpen(false);
		setSelectedPcapFilename(null);
	};

	// Get size badge class based on file size
	const getSizeBadgeClass = (fileSizeBytes) => {
		if (fileSizeBytes >= 100 * 1024 * 1024) { // >= 100MB
			return classes.sizeBadgeLarge;
		} else if (fileSizeBytes >= 10 * 1024 * 1024) { // >= 10MB
			return classes.sizeBadgeMedium;
		}
		return classes.sizeBadgeSmall;
	};

	if (hasError) {
		return (
			<Content currentMenu='reports' headerTitle='PCAP Viewer'>
				<NetworkError />
				{supportErrorMessage && (
					<div className={classes.supportErrorCard}>
						<strong>Support Error:</strong>
						<code className={classes.supportErrorCode}>{supportErrorMessage}</code>
						<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
							<button
								type='button'
								className={classes.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
							<button
								type='button'
								className={classes.copySupportErrorButton}
								onClick={() => clearPcapViewerSupportError(true)}
							>
								Clear
							</button>
						</div>
					</div>
				)}
			</Content>
		);
	}

	return (
		<Content currentMenu='reports' headerTitle='PCAP Viewer'>
			{initialLoading ? (
				<Loader />
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={classes.supportErrorBanner}>
							<div className={classes.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={classes.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={classes.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={classes.copySupportErrorButton}
									onClick={() => clearPcapViewerSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					{/* Page Header */}
					<div className={classes.pageHeader}>
						<h2>Network Packet Captures</h2>
						<div className={classes.headerInfo}>
							<span className={classes.pcapCount}>
								{pcapList.length} {pcapList.length === 1 ? 'File' : 'Files'}
							</span>
							<button 
								className={classes.refreshButton}
								onClick={handleRefresh}
								disabled={isRefreshing}
							>
								{isRefreshing ? 'Refreshing...' : 'Refresh'}
							</button>
						</div>
					</div>

					{/* Capture controls */}
					<div className={classes.captureBar}>
						<div className={classes.captureField}>
							<label htmlFor='pcapWatcher'>Watcher</label>
							<select
								id='pcapWatcher'
								value={selectedWatcher}
								onChange={(e) => setSelectedWatcher(e.target.value)}
							>
								<option value=''>Select a Watcher</option>
								{watchers.length > 0 ? (
									watchers.map((w) => (
										<option key={w[0]} value={w[0]}>{w[1]}</option>
									))
								) : (
									<option value='' disabled>No watchers available</option>
								)}
							</select>
						</div>
						<div className={classes.captureField}>
							<label htmlFor='pcapFilter'>Filter (optional BPF)</label>
							<input
								id='pcapFilter'
								type='text'
								placeholder='e.g. port 53 or host 10.0.0.5'
								value={captureFilter}
								onChange={(e) => setCaptureFilter(e.target.value)}
							/>
						</div>
						<div className={classes.captureActions}>
							<button
								type='button'
								className={classes.startCaptureButton}
								disabled={!selectedWatcher || busyAction === 'start'}
								onClick={handleStartCapture}
							>
								{busyAction === 'start' ? 'Starting…' : 'Start Capture'}
							</button>
							<button
								type='button'
								className={classes.stopCaptureButton}
								disabled={!selectedWatcher || busyAction === 'stop' || !activeOnSelected}
								onClick={handleStopCapture}
							>
								{busyAction === 'stop' ? 'Stopping…' : 'Stop Capture'}
							</button>
						</div>
						<div className={classes.captureHint}>
							Captures run on the Watcher and auto-stop after {maxMinutes} minutes.
						</div>
					</div>

					{/* PCAP List Table */}
					<div className={classes.pcapListSection}>
						<div className={classes.sectionHeader}>
							<h2>Available PCAP Files</h2>
						</div>

						{pcapList.length === 0 ? (
							<div className={classes.emptyState}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
									<path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
								</svg>
								<h3>No PCAP Files Available</h3>
								<p>Captured PCAP files will appear here once they are generated.</p>
							</div>
						) : (
							<Table
								containerStyle={{ height: '75vh', maxHeight: '800px' }}
								tableHead={
									<tr>
										<th>Filename</th>
										<th>File Size</th>
										<th>Capture Date</th>
										<th>Watcher Source</th>
										<th>Duration</th>
										<th>Status</th>
										<th>Actions</th>
									</tr>
								}
								tableRow={
									pageRows.map((pcap, index) => (
										<tr key={index}>
											<td className={classes.filenameCell}>
												{pcap.filename}
											</td>
											<td>
												<span className={`${classes.sizeBadge} ${getSizeBadgeClass(pcap.fileSizeBytes)}`}>
													{pcap.fileSize}
												</span>
											</td>
											<td className={classes.dateCell}>
												{pcap.captureDate}
											</td>
											<td className={classes.watcherCell}>
												{pcap.watcherSource}
											</td>
											<td className={classes.durationCell}>
												{pcap.duration}
											</td>
											<td>
												<span className={`${classes.statusBadge} ${classes['status_' + pcap.status] || ''}`}>
													{pcap.status}
												</span>
											</td>
											<td>
												{pcap.status === 'completed' ? (
													<div className={classes.actionButtons}>
														<Button
															label="View"
															onClick={() => handleView(pcap.filename)}
														/>
														<Button
															label="Download"
															onClick={() => handleDownload(pcap.filename)}
														/>
													</div>
												) : (
													<span className={classes.noActions}>—</span>
												)}
											</td>
										</tr>
									))
								}
							/>
						)}

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePageChange}
								rowsPerPage={pageLimit}
								totalPageSize={totalRows}
								pageNumber={pageNumber}
							/>
						</div>
					</div>

					{/* PCAP Viewer Modal */}
					<PcapModal 
						isOpen={modalOpen}
						onClose={handleModalClose}
						filename={selectedPcapFilename}
					/>
				</Fragment>
			)}
		</Content>
	);
};

export default PcapViewer;
